<template>
    <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
        <button
            v-for="option in orderedOptions"
            :key="option.id"
            type="button"
            class="rounded-xl p-3 text-left transition border-2"
            :class="[
                isSelected(option.id)
                    ? 'border-brand-purple border-4 bg-theme-card'
                    : 'border-theme-border bg-theme-card hover:border-brand-red',
                { 'opacity-60': isLocked(option.id) && !isSelected(option.id) }
            ]"
            @click="toggle(option.id)"
        >
            <div class="text-2xl mb-1">{{ option.icon }}</div>
            <div class="font-semibold text-sm text-theme-base leading-tight">
                <span v-if="isLocked(option.id)">🔒</span>
                {{ option.label }}
            </div>
            <p class="text-xs text-theme-muted mt-1 leading-tight">
                {{ option.description }}
            </p>
            <div class="flex flex-wrap gap-1 mt-2">
                <span
                    v-for="field in variantFieldsFor(option.id)"
                    :key="field"
                    class="inline-block text-xs px-2 py-0.5 rounded-full"
                    :class="badgeClass(option.id)"
                >
                    {{ field }}
                </span>
                <span
                    v-if="!variantFieldsFor(option.id).length"
                    class="inline-block text-xs px-2 py-0.5 rounded-full"
                    :class="badgeClass(option.id)"
                >
                    Sin variantes
                </span>
            </div>
        </button>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

import { canShowUpgrade } from "@/common/fields/plan-features";
import {
    PRODUCT_VARIANTS_FEATURE,
    PRODUCT_VARIANTS_PERMISSION,
    getVariantFields,
    typeRequiresVariants
} from "@/common/fields/product-types";

export default {
    name: "ProductTypeSelector",
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        options: {
            type: Array,
            required: true
        },
        value: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            initialSelection: [...this.value],
            isInternalChange: false
        };
    },
    computed: {
        ...mapState("auth", ["permissions"]),
        // Los seleccionados al cargar van primero; el orden no se recalcula
        // al hacer click, para que las cards no salten de lugar.
        orderedOptions() {
            const initial = this.initialSelection;
            return [...this.options].sort((a, b) => {
                const aSelected = initial.includes(a.id);
                const bSelected = initial.includes(b.id);
                if (aSelected === bSelected) return 0;
                return aSelected ? -1 : 1;
            });
        }
    },
    watch: {
        value(newValue) {
            if (this.isInternalChange) {
                this.isInternalChange = false;
                return;
            }
            this.initialSelection = [...newValue];
        }
    },
    methods: {
        ...mapActions("platformSubscriptions", ["openPlanUpgrade"]),
        isSelected(id) {
            return this.value.includes(id);
        },
        isLocked(id) {
            return (
                typeRequiresVariants(id) &&
                !this.$ability.hasPermission(PRODUCT_VARIANTS_PERMISSION)
            );
        },
        toggle(id) {
            if (this.isLocked(id) && !this.isSelected(id)) {
                if (
                    canShowUpgrade(this.permissions, PRODUCT_VARIANTS_FEATURE)
                ) {
                    this.openPlanUpgrade({
                        kind: "feature",
                        feature: PRODUCT_VARIANTS_FEATURE
                    });
                }
                return;
            }
            this.isInternalChange = true;
            this.$emit(
                "input",
                this.isSelected(id)
                    ? this.value.filter((item) => item !== id)
                    : [...this.value, id]
            );
        },
        variantFieldsFor(id) {
            return getVariantFields(id);
        },
        badgeClass(id) {
            return this.isSelected(id)
                ? "bg-brand-purple text-white"
                : "bg-theme-hover text-theme-muted";
        }
    }
};
</script>
